import { useEffect, useState, useCallback } from 'react'
import { Users, Plus, X, Trash2, RefreshCw } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface Tt {
  id: number
  name: string
  scenario: string
  facilitator: string
  participants: string[]
  held_at: string
  duration_minutes: number
  gaps_found: string
  actions_assigned: string
  next_due: string | null
}

export default function TabletopPage() {
  const [items, setItems] = useState<Tt[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({
    name: '', scenario: '', facilitator: '', participants: '',
    held_at: new Date().toISOString().slice(0, 16),
    duration_minutes: 60, gaps_found: '', actions_assigned: '', next_due: '',
  })

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const r = await api.get('/api/res/tabletops')
      setItems(r.data?.exercises || [])
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])

  const create = async () => {
    if (!form.name) return
    await api.post('/api/res/tabletops', {
      ...form,
      participants: form.participants.split(',').map(p => p.trim()).filter(Boolean),
      held_at: new Date(form.held_at).toISOString(),
      next_due: form.next_due ? new Date(form.next_due).toISOString() : undefined,
    })
    setShowForm(false)
    setForm({ ...form, name: '', scenario: '', gaps_found: '', actions_assigned: '' })
    fetchAll()
  }

  const remove = async (id: number) => {
    if (!confirm('Delete?')) return
    await api.delete(`/api/res/tabletops/${id}`)
    fetchAll()
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Users size={24} className="text-purple-300" /> Tabletop Exercises
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Log scenarios run, gaps found, actions assigned, when to repeat.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={fetchAll} className="btn-ghost text-xs"><RefreshCw size={11} className={loading ? 'animate-spin' : ''} /></button>
          <button type="button" onClick={() => setShowForm(true)} className="btn-primary text-xs"><Plus size={12} /> Log exercise</button>
        </div>
      </div>

      {showForm && (
        <div className="card p-4 border border-blue/40">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-text-primary">Log tabletop</h3>
            <button type="button" onClick={() => setShowForm(false)} className="text-text-muted hover:text-text-primary"><X size={14} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
            <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder='Name e.g. "Q2 ransomware tabletop"' className="input py-1.5 md:col-span-2" />
            <input value={form.scenario} onChange={e => setForm({...form, scenario: e.target.value})} placeholder="Scenario" className="input py-1.5" />
            <input value={form.facilitator} onChange={e => setForm({...form, facilitator: e.target.value})} placeholder="Facilitator" className="input py-1.5" />
            <input value={form.participants} onChange={e => setForm({...form, participants: e.target.value})} placeholder="Participants (comma-separated)" className="input py-1.5 md:col-span-2" />
            <label className="space-y-0.5"><span className="text-text-muted">Held at</span>
              <input type="datetime-local" value={form.held_at} onChange={e => setForm({...form, held_at: e.target.value})} className="input w-full py-1.5" /></label>
            <input type="number" value={form.duration_minutes} onChange={e => setForm({...form, duration_minutes: parseInt(e.target.value, 10)})} placeholder="Duration (min)" className="input py-1.5" />
            <textarea value={form.gaps_found} onChange={e => setForm({...form, gaps_found: e.target.value})} rows={3} placeholder="Gaps found" className="input py-1.5 md:col-span-2" />
            <textarea value={form.actions_assigned} onChange={e => setForm({...form, actions_assigned: e.target.value})} rows={3} placeholder="Actions assigned (owner / due)" className="input py-1.5 md:col-span-2" />
            <label className="space-y-0.5"><span className="text-text-muted">Next due</span>
              <input type="datetime-local" value={form.next_due} onChange={e => setForm({...form, next_due: e.target.value})} className="input w-full py-1.5" /></label>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button type="button" onClick={() => setShowForm(false)} className="btn-ghost text-xs">Cancel</button>
            <button type="button" onClick={create} className="btn-primary text-xs">Save</button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {items.length === 0 && (
          <p className="card p-6 text-center text-text-muted text-[11px] italic">No tabletops logged.</p>
        )}
        {items.map(t => {
          const overdue = t.next_due && new Date(t.next_due) < new Date()
          return (
            <div key={t.id} className={clsx('card p-3', overdue && 'border-amber/40 bg-amber/5')}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <h3 className="text-sm font-semibold text-text-primary">{t.name}</h3>
                  <div className="text-[10px] text-text-muted mt-0.5">
                    {t.scenario && <>{t.scenario} · </>}
                    👤 {t.facilitator || '—'} · {t.duration_minutes}min · held {t.held_at?.slice(0, 10)}
                    {t.next_due && <> · next {t.next_due.slice(0, 10)}{overdue ? ' (overdue)' : ''}</>}
                  </div>
                  {t.participants?.length > 0 && (
                    <div className="text-[10px] text-text-secondary mt-1">{t.participants.join(', ')}</div>
                  )}
                </div>
                <button type="button" onClick={() => remove(t.id)} className="text-text-muted hover:text-rose"><Trash2 size={11} /></button>
              </div>
              {(t.gaps_found || t.actions_assigned) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2 text-[11px]">
                  {t.gaps_found && (
                    <div>
                      <div className="text-[9px] uppercase text-amber-light font-semibold">Gaps</div>
                      <p className="text-text-secondary mt-0.5 whitespace-pre-line">{t.gaps_found}</p>
                    </div>
                  )}
                  {t.actions_assigned && (
                    <div>
                      <div className="text-[9px] uppercase text-blue-light font-semibold">Actions</div>
                      <p className="text-text-secondary mt-0.5 whitespace-pre-line">{t.actions_assigned}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
